import { useNavigate } from 'react-router-dom'
import {
    Server, HardDrive, MapPin, Users, BookOpen, ShieldAlert,
    Activity, ArrowRight, CheckCircle2, Clock, AlertTriangle, Database,
    BarChart3, Layers, Shield, ArrowUpRight, Cpu, Network, ScanLine,
    FileCheck, AlertCircle, CircleDot
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMe } from '@/features/auth/hooks/useAuth'
import { useDashboardOverview } from '@/features/dashboard/hooks'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'

export function DashboardPage() {
    const navigate = useNavigate()
    const { data: user } = useMe()
    const { data: overview, isLoading } = useDashboardOverview()
    const { t } = useTranslation()

    // Merge API data over defaults so a partial response (e.g. missing
    // runbookTaskStats / securityScans) can't crash the dashboard — previously
    // `overview || {defaults}` only applied defaults when the WHOLE response
    // was falsy, so a present-but-incomplete payload threw
    // "Cannot read properties of undefined (reading 'pending')".
    const DASHBOARD_DEFAULTS = {
        objects: 0, services: 0, versions: 0, redObjects: 0, amberObjects: 0,
        greenObjects: 0, greyObjects: 0, locations: 0, vaults: 0, users: 0,
        masterRunbooks: 0, objectRunbooks: 0, scenarios: 0, teams: 0, technologies: 0,
        openRecoveries: 0, completedRecoveries: 0, pendingApprovals: 0,
        runbookTaskStats: { pending: 0, started: 0, completed: 0, issue: 0 },
        runbookCoverage: 0,
        securityScans: { scanned: 0, anomalies: 0, total: 0 },
        objectsByLocation: [], objectsByLayer: [], recoveryByMonth: [], recentRecoveries: [],
    }
    const ov = (overview || {}) as Partial<typeof DASHBOARD_DEFAULTS>
    const o = {
        ...DASHBOARD_DEFAULTS,
        ...ov,
        // Nested objects: merge so a partial/absent nested payload keeps defaults
        runbookTaskStats: { ...DASHBOARD_DEFAULTS.runbookTaskStats, ...(ov.runbookTaskStats || {}) },
        securityScans: { ...DASHBOARD_DEFAULTS.securityScans, ...(ov.securityScans || {}) },
    }

    // Object health donut
    const healthDonut = useMemo((): EChartsOption => {
        const total = o.greenObjects + o.amberObjects + o.redObjects + o.greyObjects
        if (total === 0) return {}
        return {
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10 } },
            series: [{
                type: 'pie', radius: ['52%', '78%'], center: ['50%', '42%'],
                label: { show: true, position: 'center', fontSize: 24, fontWeight: 'bold', formatter: () => `${total}` },
                data: [
                    { value: o.greenObjects, name: 'Green', itemStyle: { color: '#22c55e' } },
                    { value: o.amberObjects, name: 'Amber', itemStyle: { color: '#f59e0b' } },
                    { value: o.redObjects, name: 'Red', itemStyle: { color: '#ef4444' } },
                    { value: o.greyObjects, name: 'Grey', itemStyle: { color: '#9ca3af' } },
                ],
            }],
        }
    }, [o.greenObjects, o.amberObjects, o.redObjects, o.greyObjects])

    // Recovery donut
    const recoveryDonut = useMemo((): EChartsOption => {
        const total = o.openRecoveries + o.completedRecoveries + o.pendingApprovals
        if (total === 0) return {}
        return {
            tooltip: { trigger: 'item' },
            legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10 } },
            series: [{
                type: 'pie', radius: ['52%', '78%'], center: ['50%', '42%'],
                label: { show: true, position: 'center', fontSize: 24, fontWeight: 'bold', formatter: () => `${total}` },
                data: [
                    { value: o.openRecoveries, name: 'Open', itemStyle: { color: '#3b82f6' } },
                    { value: o.completedRecoveries, name: 'Completed', itemStyle: { color: '#22c55e' } },
                    { value: o.pendingApprovals, name: 'Pending', itemStyle: { color: '#f59e0b' } },
                ],
            }],
        }
    }, [o.openRecoveries, o.completedRecoveries, o.pendingApprovals])

    // Objects by location bar chart
    const locationChart = useMemo((): EChartsOption => {
        if (!o.objectsByLocation?.length) return {}
        const locs = o.objectsByLocation
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: 10, right: 30, top: 8, bottom: 5, containLabel: true },
            xAxis: { type: 'value', show: false },
            yAxis: {
                type: 'category', data: locs.map((l) => l.location).reverse(),
                axisLine: { show: false }, axisTick: { show: false },
                axisLabel: { fontSize: 11, color: '#888' },
            },
            series: [{
                type: 'bar', data: locs.map((l) => l.count).reverse(), barWidth: 14,
                itemStyle: { borderRadius: [0, 4, 4, 0], color: '#6366f1' },
                label: { show: true, position: 'right', fontSize: 11, color: '#666' },
            }],
        }
    }, [o.objectsByLocation])

    // Objects by layer bar chart
    const layerChart = useMemo((): EChartsOption => {
        if (!o.objectsByLayer?.length) return {}
        const layers = o.objectsByLayer
        return {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: 10, right: 20, top: 8, bottom: 5, containLabel: true },
            xAxis: { type: 'value', show: false },
            yAxis: {
                type: 'category', data: layers.map((l) => l.layer).reverse(),
                axisLine: { show: false }, axisTick: { show: false },
                axisLabel: { fontSize: 11, color: '#888' },
            },
            series: [{
                type: 'bar', data: layers.map((l) => l.count).reverse(), barWidth: 14,
                itemStyle: { borderRadius: [0, 4, 4, 0], color: '#0ea5e9' },
                label: { show: true, position: 'right', fontSize: 11, color: '#666' },
            }],
        }
    }, [o.objectsByLayer])

    // Runbook task gauge
    const taskTotal = o.runbookTaskStats.pending + o.runbookTaskStats.started + o.runbookTaskStats.completed + o.runbookTaskStats.issue
    const taskCompletePct = taskTotal > 0 ? Math.round((o.runbookTaskStats.completed / taskTotal) * 100) : 0

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        )
    }

    return (
        <div className="space-y-5 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('dashboard.welcome')}, {user?.name || 'User'}</h1>
                    <p className="text-sm text-muted-foreground">{t('dashboard.overview')}</p>
                </div>
                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5"><Cpu className="h-4 w-4" /><span>{o.technologies} {t('dashboard.technologies')}</span></div>
                    <div className="flex items-center gap-1.5"><Users className="h-4 w-4" /><span>{o.teams} {t('dashboard.teams')}</span></div>
                    <div className="flex items-center gap-1.5"><Layers className="h-4 w-4" /><span>{o.scenarios} {t('dashboard.scenarios')}</span></div>
                </div>
            </div>

            {/* Row 1: Key Metrics Strip */}
            <div className="grid gap-3 grid-cols-3 lg:grid-cols-6">
                {[
                    { label: t('dashboard.metrics.objects'), value: o.objects, sub: `${o.services} ${t('dashboard.metrics.services')}`, icon: Server, color: 'text-blue-600', bg: 'bg-blue-50', path: '/objects' },
                    { label: t('dashboard.metrics.versions'), value: o.versions.toLocaleString(), icon: Database, color: 'text-indigo-600', bg: 'bg-indigo-50', path: '/objects' },
                    { label: t('dashboard.metrics.locations'), value: o.locations, icon: MapPin, color: 'text-emerald-600', bg: 'bg-emerald-50', path: '/locations' },
                    { label: t('dashboard.metrics.vaults'), value: o.vaults, icon: HardDrive, color: 'text-violet-600', bg: 'bg-violet-50', path: '/vaults' },
                    { label: t('dashboard.metrics.runbooks'), value: o.masterRunbooks + o.objectRunbooks, sub: `${o.masterRunbooks} ${t('dashboard.metrics.master')} \u00b7 ${o.objectRunbooks} ${t('dashboard.metrics.object')}`, icon: BookOpen, color: 'text-amber-600', bg: 'bg-amber-50', path: '/objects' },
                    { label: t('dashboard.metrics.users'), value: o.users, icon: Users, color: 'text-sky-600', bg: 'bg-sky-50', path: '/users' },
                ].map((item) => (
                    <Card key={item.label} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(item.path)}>
                        <CardContent className="p-3.5 flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}>
                                <item.icon className={`h-4 w-4 ${item.color}`} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                                <p className={`text-lg font-bold leading-tight ${item.color}`}>{item.value}</p>
                                {'sub' in item && item.sub && <p className="text-[10px] text-muted-foreground">{item.sub}</p>}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Row 2: Health + Recovery + Security */}
            <div className="grid gap-5 grid-cols-1 lg:grid-cols-3">
                {/* Object Health */}
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/objects')}>
                    <CardHeader className="pb-1 pt-4 px-5">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.objectHealth')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                        <div className="grid grid-cols-2 gap-2 items-center">
                            <div>
                                <ReactECharts option={healthDonut} style={{ height: 170 }} opts={{ renderer: 'canvas' }} />
                            </div>
                            <div className="space-y-2.5">
                                {[
                                    { label: t('dashboard.status.green'), value: o.greenObjects, color: 'bg-green-500' },
                                    { label: t('dashboard.status.amber'), value: o.amberObjects, color: 'bg-amber-500' },
                                    { label: t('dashboard.status.red'), value: o.redObjects, color: 'bg-red-500' },
                                    { label: t('dashboard.status.grey'), value: o.greyObjects, color: 'bg-gray-400' },
                                ].map((s) => (
                                    <div key={s.label} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                                            <span className="text-xs text-muted-foreground">{s.label}</span>
                                        </div>
                                        <span className="text-sm font-semibold">{s.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Recovery Status */}
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/recovery-management')}>
                    <CardHeader className="pb-1 pt-4 px-5">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Activity className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.recoveryStatus')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                        <div className="grid grid-cols-2 gap-2 items-center">
                            <div>
                                {(o.openRecoveries + o.completedRecoveries + o.pendingApprovals) > 0 ? (
                                    <ReactECharts option={recoveryDonut} style={{ height: 170 }} opts={{ renderer: 'canvas' }} />
                                ) : (
                                    <div className="flex items-center justify-center h-[170px] text-xs text-muted-foreground">{t('dashboard.noRecoveries')}</div>
                                )}
                            </div>
                            <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2"><div className="h-2.5 w-2.5 rounded-full bg-blue-500" /><span className="text-xs text-muted-foreground">{t('dashboard.status.open')}</span></div>
                                    <span className="text-sm font-semibold">{o.openRecoveries}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2"><div className="h-2.5 w-2.5 rounded-full bg-green-500" /><span className="text-xs text-muted-foreground">{t('dashboard.status.completed')}</span></div>
                                    <span className="text-sm font-semibold">{o.completedRecoveries}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2"><div className="h-2.5 w-2.5 rounded-full bg-amber-500" /><span className="text-xs text-muted-foreground">{t('dashboard.status.pending')}</span></div>
                                    <span className="text-sm font-semibold">{o.pendingApprovals}</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Security & Runbook Coverage */}
                <div className="flex flex-col gap-4">
                    <Card className="flex-1 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/objects')}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="h-8 w-8 rounded-lg bg-rose-50 flex items-center justify-center">
                                    <ScanLine className="h-4 w-4 text-rose-600" />
                                </div>
                                <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('dashboard.securityScans')}</p>
                                    <p className="text-lg font-bold leading-tight text-rose-600">{o.securityScans.scanned}</p>
                                </div>
                                <div className="ml-auto text-right">
                                    <p className="text-xs text-muted-foreground">{t('dashboard.ofVersions', { count: o.securityScans.total })}</p>
                                    {o.securityScans.anomalies > 0 && (
                                        <p className="text-xs font-semibold text-red-600">{t('dashboard.anomalies', { count: o.securityScans.anomalies })}</p>
                                    )}
                                </div>
                            </div>
                            {/* Scan coverage bar */}
                            <div className="w-full bg-muted rounded-full h-1.5">
                                <div
                                    className="bg-rose-500 h-1.5 rounded-full transition-all"
                                    style={{ width: `${o.securityScans.total > 0 ? Math.round((o.securityScans.scanned / o.securityScans.total) * 100) : 0}%` }}
                                />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/master-runbooks')}>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="h-8 w-8 rounded-lg bg-teal-50 flex items-center justify-center">
                                    <FileCheck className="h-4 w-4 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{t('dashboard.runbookCoverage')}</p>
                                    <p className="text-lg font-bold leading-tight text-teal-600">{o.runbookCoverage}%</p>
                                </div>
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                                <div className="bg-teal-500 h-1.5 rounded-full transition-all" style={{ width: `${o.runbookCoverage}%` }} />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Row 3: Active Recovery Tasks (if any open recoveries) */}
            {taskTotal > 0 && (
                <Card>
                    <CardHeader className="pb-2 pt-4 px-5">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <CircleDot className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.activeRecoveryTasks')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                        <div className="grid grid-cols-4 gap-4">
                            {[
                                { label: t('dashboard.status.pending'), value: o.runbookTaskStats.pending, color: 'text-slate-600', bar: 'bg-slate-400' },
                                { label: t('dashboard.status.inProgress'), value: o.runbookTaskStats.started, color: 'text-blue-600', bar: 'bg-blue-500' },
                                { label: t('dashboard.status.completed'), value: o.runbookTaskStats.completed, color: 'text-green-600', bar: 'bg-green-500' },
                                { label: t('dashboard.status.issues'), value: o.runbookTaskStats.issue, color: 'text-red-600', bar: 'bg-red-500' },
                            ].map((s) => (
                                <div key={s.label}>
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="text-xs text-muted-foreground">{s.label}</span>
                                        <span className={`text-lg font-bold ${s.color}`}>{s.value}</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-1.5">
                                        <div className={`${s.bar} h-1.5 rounded-full transition-all`} style={{ width: `${taskTotal > 0 ? Math.round((s.value / taskTotal) * 100) : 0}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                            <span>{t('dashboard.totalTasks', { count: taskTotal })}</span>
                            <span className="font-semibold text-green-600">{t('dashboard.percentComplete', { pct: taskCompletePct })}</span>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Row 4: Charts */}
            <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
                {/* Objects by Location */}
                <Card>
                    <CardHeader className="pb-1 pt-4 px-5">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.objectsByLocation')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-3">
                        {o.objectsByLocation.length > 0 ? (
                            <ReactECharts option={locationChart} style={{ height: 220 }} opts={{ renderer: 'canvas' }} />
                        ) : (
                            <div className="flex items-center justify-center h-[220px] text-sm text-muted-foreground">{t('dashboard.noData')}</div>
                        )}
                    </CardContent>
                </Card>

                {/* Objects by Layer */}
                <Card>
                    <CardHeader className="pb-1 pt-4 px-5">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Layers className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.objectsByLayer')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-3">
                        {o.objectsByLayer.length > 0 ? (
                            <ReactECharts option={layerChart} style={{ height: 220 }} opts={{ renderer: 'canvas' }} />
                        ) : (
                            <div className="flex items-center justify-center h-[220px] text-sm text-muted-foreground">{t('dashboard.noData')}</div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Row 5: Recent Recoveries */}
            <Card>
                <CardHeader className="pb-2 pt-4 px-5">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {t('dashboard.recentRecoveryRequests')}
                        </CardTitle>
                        <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => navigate('/recovery-management')}>
                            {t('dashboard.viewAll')} <ArrowRight className="h-3 w-3" />
                        </button>
                    </div>
                </CardHeader>
                <CardContent className="px-5 pb-4">
                    {o.recentRecoveries.length > 0 ? (
                        <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                            {o.recentRecoveries.map((r, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center gap-3 px-3 py-2.5 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => navigate('/recovery-management')}
                                >
                                    {r.status === 'Open' ? (
                                        <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                    ) : (
                                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">{r.objectID}</p>
                                        <p className="text-[10px] text-muted-foreground">
                                            {r.dateRequested && new Date(r.dateRequested).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${r.status === 'Open' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                        {r.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-sm text-muted-foreground py-6">{t('dashboard.noRecentRecoveries')}</div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
